import { Avatar, Box, Grid, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Header from "../../components/Header";
import { authFetch } from "../../Utils/apiClient";

function ViewAyfaMember() {
  const { id } = useParams();
  const [memberDetails, setMemberDetails] = useState(null);

  useEffect(() => {
    const fetchMemberDetails = async () => {
      try {
        const response = await authFetch(`/user/getUserById/${id}`);
        if (!response.ok) {
          throw new Error("Failed to fetch member details");
        }
        const memberData = await response.json();

        const {
          email,
          streetNumber,
          streetName,
          APT,
          city,
          state,
          country,
          zipCode,
          imageUrl,
          Role,
          createdAt,
          registered,
          IsVerified,
          updatedAt,
          RoleId,
          pwdotpExpiryTime,
          profileName,
          Ayfabora,
          packageName,
        } = memberData.result;

        const formattedMemberDetails = {
          email,
          streetNumber,
          streetName,
          APT,
          city,
          state,
          country,
          zipCode,
          imageUrl,
          role: Role.role,
          createdAt,
          registered,
          isVerified: IsVerified,
          updatedAt,
          RoleId,
          pwdotpExpiryTime,
          profileName,
          packageName,
          // Ayfa specific information
          coverageType: Ayfabora?.coveragetype,
          groupType: Ayfabora?.grouptype,
          formulaType: Ayfabora?.formulatype,
          insuredFirstName: Ayfabora?.insuredfirstname,
          insuredMiddleName: Ayfabora?.insuredmiddlename,
          insuredLastName: Ayfabora?.insuredlastname,
          insuredFullName: Ayfabora?.insurefullname,
          dob: Ayfabora?.afyadob,
          age: Ayfabora?.afyage,
          gender: Ayfabora?.afyagender,
          maritalStatus: Ayfabora?.afyamaritalstatus,
          occupation: Ayfabora?.afyaoccupation,
          employerName: Ayfabora?.empname,
          collegeName: Ayfabora?.collegename,
          businessName: Ayfabora?.bussinessname,
          afyaRole: Ayfabora?.afyaRole,
          amount: Ayfabora?.amount,
          date: Ayfabora?.date,
          // Spouse information
          spouseFirstName: Ayfabora?.sposefirstname,
          spouseLastName: Ayfabora?.sposelastname,
          spouseDob: Ayfabora?.sposedob,
          spouseAge: Ayfabora?.sposeage,
          spouseGender: Ayfabora?.sposegender,
          spouseEmail: Ayfabora?.sposeemail,
          // Primary beneficiary
          primaryBeneficiaryFirstName: Ayfabora?.primarybeneficiaryfirstname,
          primaryBeneficiaryLastName: Ayfabora?.primarybeneficiarylastname,
          primaryBeneficiaryDob: Ayfabora?.primarybeneficiarydob,
          primaryBeneficiaryAge: Ayfabora?.primarybeneficiaryage,
          primaryBeneficiaryGender: Ayfabora?.primarybeneficiarygender,
          primaryBeneficiaryEmail: Ayfabora?.primarybeneficiaryemail,
          primaryBeneficiaryRelationship: Ayfabora?.primarybeneficiaryrelationship,
          // Children and Beneficiaries
          children: Ayfabora?.Children || [],
          beneficiaries: Ayfabora?.Benificiaries || []
        };

        setMemberDetails(formattedMemberDetails);
      } catch (error) {
        console.error("Error fetching member details:", error);
      }
    };

    fetchMemberDetails();
  }, [id]);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      weekday: "long",
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "numeric",
      second: "numeric",
      hour12: true,
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return "N/A";
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const renderDetails = () => {
    if (!memberDetails) return null;

    const basicDetails = [
      { label: "Profile Name", value: memberDetails.profileName },
      { label: "Email", value: memberDetails.email },
      { label: "Role", value: memberDetails.role },
      { label: "Registered", value: memberDetails.registered ? "Yes" : "No" },
      { label: "Verified", value: memberDetails.isVerified ? "Yes" : "No" },
    ];

    const addressDetails = [
      { label: "Street Number", value: memberDetails.streetNumber },
      { label: "Street Name", value: memberDetails.streetName },
      { label: "APT", value: memberDetails.APT },
      { label: "City", value: memberDetails.city },
      { label: "State", value: memberDetails.state },
      { label: "Country", value: memberDetails.country },
      { label: "Zip Code", value: memberDetails.zipCode },
    ];

    const insuranceDetails = [
      { label: "Coverage Type", value: memberDetails.coverageType },
      { label: "Group Type", value: memberDetails.groupType },
      { label: "Formula Type", value: memberDetails.formulaType },
      { label: "Amount", value: formatCurrency(memberDetails.amount) },
      { label: "Application Date", value: formatDate(memberDetails.date) },
    ];

    const insuredDetails = [
      { label: "First Name", value: memberDetails.insuredFirstName },
      { label: "Middle Name", value: memberDetails.insuredMiddleName },
      { label: "Last Name", value: memberDetails.insuredLastName },
      { label: "Full Name", value: memberDetails.insuredFullName },
      { label: "Date of Birth", value: formatDate(memberDetails.dob) },
      { label: "Age", value: memberDetails.age },
      { label: "Gender", value: memberDetails.gender },
      { label: "Marital Status", value: memberDetails.maritalStatus },
      { label: "Occupation", value: memberDetails.occupation },
      { label: "Ayfa Role", value: memberDetails.afyaRole },
    ];

    const employmentDetails = [
      { label: "Employer Name", value: memberDetails.employerName },
      { label: "College Name", value: memberDetails.collegeName },
      { label: "Business Name", value: memberDetails.businessName },
    ].filter(item => item.value && item.value !== "N/A");

    const spouseDetails = memberDetails.spouseFirstName ? [
      { label: "Spouse First Name", value: memberDetails.spouseFirstName },
      { label: "Spouse Last Name", value: memberDetails.spouseLastName },
      { label: "Spouse Date of Birth", value: formatDate(memberDetails.spouseDob) },
      { label: "Spouse Age", value: memberDetails.spouseAge },
      { label: "Spouse Gender", value: memberDetails.spouseGender },
      { label: "Spouse Email", value: memberDetails.spouseEmail },
    ] : [];

    const primaryBeneficiaryDetails = memberDetails.primaryBeneficiaryFirstName ? [
      { label: "Primary Beneficiary First Name", value: memberDetails.primaryBeneficiaryFirstName },
      { label: "Primary Beneficiary Last Name", value: memberDetails.primaryBeneficiaryLastName },
      { label: "Primary Beneficiary DOB", value: formatDate(memberDetails.primaryBeneficiaryDob) },
      { label: "Primary Beneficiary Age", value: memberDetails.primaryBeneficiaryAge },
      { label: "Primary Beneficiary Gender", value: memberDetails.primaryBeneficiaryGender },
      { label: "Primary Beneficiary Email", value: memberDetails.primaryBeneficiaryEmail },
      { label: "Primary Beneficiary Relationship", value: memberDetails.primaryBeneficiaryRelationship },
    ] : [];

    const systemDetails = [
      { label: "Created At", value: formatDateTime(memberDetails.createdAt) },
      { label: "Updated At", value: formatDateTime(memberDetails.updatedAt) },
      { label: "Role ID", value: memberDetails.RoleId },
    ];

    // Combine all detail sections
    const allSections = [
      { title: "Basic Information", items: basicDetails },
      { title: "Address Information", items: addressDetails },
      { title: "Insurance Details", items: insuranceDetails },
      { title: "Insured Information", items: insuredDetails },
      { title: "Employment/Education", items: employmentDetails },
      { title: "Spouse Information", items: spouseDetails },
      { title: "Primary Beneficiary", items: primaryBeneficiaryDetails },
      { title: "System Information", items: systemDetails },
    ].filter(section => section.items.length > 0);

    return (
      <Grid container spacing={3} justifyContent="center">
        <Grid item xs={12} md={10}>
          {allSections.map((section) => (
            <Box key={section.title} mb={4}>
              <Typography variant="h6" sx={{ fontWeight: "bold", marginBottom: 2, color: "primary.main" }}>
                {section.title}
              </Typography>
              {section.items.map((item) => (
                <Grid container spacing={1} key={item.label} sx={{ marginBottom: 1 }}>
                  <Grid item xs={6} md={4}>
                    <Typography variant="body1" sx={{ fontSize: "1rem", fontWeight: "bold" }}>
                      {item.label}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={8}>
                    <Typography variant="body1" sx={{ fontSize: "1rem" }}>
                      {item.value || "Not provided"}
                    </Typography>
                  </Grid>
                </Grid>
              ))}
            </Box>
          ))}

          {/* Children Section */}
          {memberDetails.children.length > 0 && (
            <Box mb={4}>
              <Typography variant="h6" sx={{ fontWeight: "bold", marginBottom: 2, color: "primary.main" }}>
                Children
              </Typography>
              {memberDetails.children.map((child, index) => (
                <Box key={child.id} mb={2} sx={{ pl: 2, borderLeft: 2, borderColor: 'primary.light' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: "bold", marginBottom: 1 }}>
                    Child {index + 1}
                  </Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={6} md={4}>
                      <Typography variant="body2" sx={{ fontWeight: "bold" }}>Name</Typography>
                    </Grid>
                    <Grid item xs={6} md={8}>
                      <Typography variant="body2">{child.name || "Not provided"}</Typography>
                    </Grid>
                    <Grid item xs={6} md={4}>
                      <Typography variant="body2" sx={{ fontWeight: "bold" }}>Date of Birth</Typography>
                    </Grid>
                    <Grid item xs={6} md={8}>
                      <Typography variant="body2">{formatDate(child.dob)}</Typography>
                    </Grid>
                    <Grid item xs={6} md={4}>
                      <Typography variant="body2" sx={{ fontWeight: "bold" }}>Gender</Typography>
                    </Grid>
                    <Grid item xs={6} md={8}>
                      <Typography variant="body2">{child.gender || "Not provided"}</Typography>
                    </Grid>
                  </Grid>
                </Box>
              ))}
            </Box>
          )}

          {/* Beneficiaries Section */}
          {memberDetails.beneficiaries.length > 0 && (
            <Box mb={4}>
              <Typography variant="h6" sx={{ fontWeight: "bold", marginBottom: 2, color: "primary.main" }}>
                Secondary Beneficiaries
              </Typography>
              {memberDetails.beneficiaries.map((beneficiary, index) => (
                <Box key={beneficiary.id} mb={2} sx={{ pl: 2, borderLeft: 2, borderColor: 'secondary.light' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: "bold", marginBottom: 1 }}>
                    Beneficiary {index + 1}
                  </Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={6} md={4}>
                      <Typography variant="body2" sx={{ fontWeight: "bold" }}>First Name</Typography>
                    </Grid>
                    <Grid item xs={6} md={8}>
                      <Typography variant="body2">{beneficiary.secondarybenificiaryfirstname}</Typography>
                    </Grid>
                    <Grid item xs={6} md={4}>
                      <Typography variant="body2" sx={{ fontWeight: "bold" }}>Last Name</Typography>
                    </Grid>
                    <Grid item xs={6} md={8}>
                      <Typography variant="body2">{beneficiary.secondarybenificiarylastname}</Typography>
                    </Grid>
                    <Grid item xs={6} md={4}>
                      <Typography variant="body2" sx={{ fontWeight: "bold" }}>Date of Birth</Typography>
                    </Grid>
                    <Grid item xs={6} md={8}>
                      <Typography variant="body2">{formatDate(beneficiary.secondarybenificiarydob)}</Typography>
                    </Grid>
                    <Grid item xs={6} md={4}>
                      <Typography variant="body2" sx={{ fontWeight: "bold" }}>Age</Typography>
                    </Grid>
                    <Grid item xs={6} md={8}>
                      <Typography variant="body2">{beneficiary.secondarybenificiaryage}</Typography>
                    </Grid>
                    <Grid item xs={6} md={4}>
                      <Typography variant="body2" sx={{ fontWeight: "bold" }}>Gender</Typography>
                    </Grid>
                    <Grid item xs={6} md={8}>
                      <Typography variant="body2">{beneficiary.secondarybenificiarygender}</Typography>
                    </Grid>
                    <Grid item xs={6} md={4}>
                      <Typography variant="body2" sx={{ fontWeight: "bold" }}>Email</Typography>
                    </Grid>
                    <Grid item xs={6} md={8}>
                      <Typography variant="body2">{beneficiary.secondarybenificiaryemail}</Typography>
                    </Grid>
                    <Grid item xs={6} md={4}>
                      <Typography variant="body2" sx={{ fontWeight: "bold" }}>Relationship</Typography>
                    </Grid>
                    <Grid item xs={6} md={8}>
                      <Typography variant="body2">{beneficiary.secondarybenificiaryrelationship}</Typography>
                    </Grid>
                  </Grid>
                </Box>
              ))}
            </Box>
          )}
        </Grid>
      </Grid>
    );
  };

  return (
    <Box p="20px">
      <Header
        title={`View Ayfa Member ID ${id}`}
        subtitle={memberDetails ? `Name: ${memberDetails.insuredFullName}` : ""}
      />
      <Box m="10px 0 0 0" height="75vh" overflow="auto">
        {renderDetails()}
      </Box>
    </Box>
  );
}

export default ViewAyfaMember;